import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getDocumentsNeedToUpload } from 'store';
import { useThunk } from 'hooks/useThunk';
import UploadInvestmentProofsTemplate from 'components/Templates/MyPay/UploadInvestmentProofs';
import { uploadMyInvestmentProofsAction } from 'api/home/myPay';
import { Box, Typography, Skeleton } from '@mui/material';
import useToast from 'components/Provider/useToast';
import { isEmpty } from 'lodash';
import { formDataApi } from 'api/api';
import { hasPermission } from 'utils/permissions';

const InvestmentProofPage = () => {
  const addToast = useToast();
  const navigate = useNavigate();
  const authorities = useSelector((state) => state.session.user.authorities);

  const [getDocumentsList, isLoadingDocumentsList, loadingDocumentsListError] = useThunk(getDocumentsNeedToUpload);
  useEffect(() => {
    getDocumentsList();
  }, [getDocumentsList]);

  const { docsList } = useSelector((state) => state.uploadInvestmentProofs);

  const docsListData = docsList?.data;

  if (isLoadingDocumentsList) {
    return (
      <Box sx={{ p: 4 }}>
        <Skeleton height={70} />
        <Skeleton />
        <Skeleton />
      </Box>
    );
  }

  if (loadingDocumentsListError !== null) {
    return (
      <Typography variant="h6">
        {loadingDocumentsListError?.message || 'Error while fetching documents list.'}
      </Typography>
    );
  }

  const employeeDeductionDeclarationInstance = docsListData?.employeeDeductionDeclarationInstance;
  const deductionHeadsList = docsListData?.deductionHeadsList;
  const dedecHeadDeclrAmtsMap = docsListData?.dedecHeadDeclrAmtsMap;
  const secRefDetailsMap = docsListData?.secRefDetailsMap;
  const enumValues = {
    PAYROLL_OPEN_FOR_PROOF_SUBMISSION: 'Open for Proof Submission',
    PAYROLL_PROOF_SUBMISSION_DRAFT: 'Draft Proofs',
  };

  const showProofSubmission =
    employeeDeductionDeclarationInstance?.proofSubmissionStatus === enumValues.PAYROLL_PROOF_SUBMISSION_DRAFT;
  const userHasProofSubmitPermission =
    hasPermission(authorities, ['ROLE_BASE_PERMISSION_PAY_INDEX_SUBMIT_PROOFS']) && showProofSubmission;

  const userhasProofUploadPermission =
    hasPermission(authorities, ['ROLE_BASE_PERMISSION_PAY_INDEX_UPLOAD_MY_INVESTMENT_PROOFS']) && showProofSubmission;

  const handleUpload = async ({ files }) => {
    const body = new FormData();
    Object.keys(files).forEach((key) => {
      if (key === 'form12bb') {
        body.append(`form12bb`, files[key]);
      } else body.append(`investmentProof[${key}]`, files[key]);
    });
    body.append(`did`, docsListData?.employeeDeductionDeclarationInstance?.id);
    body.append(`eid`, docsListData?.employeeDeductionDeclarationInstance?.employee?.id);

    const deduction1013A = deductionHeadsList?.find((data) => data.statutoryReferenceSection === '10(13A)');
    if (!isEmpty(deduction1013A)) {
      body.append(`dheadId1013A`, deduction1013A?.id);
    }

    const dhead24 = deductionHeadsList?.find((data) => data.statutoryReferenceSection === '24');
    if (!isEmpty(dhead24)) {
      body.append(`dheadId24`, dhead24?.id);
    }

    try {
      const response = await uploadMyInvestmentProofsAction(body);
      if (response.status === 200) {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.data || 'Uploaded document successfully',
        });
        navigate('/home/mypay/tds-declaration');
      }
    } catch (err) {
      addToast({ type: 'error', title: 'ERROR', message: err?.message || 'Failed to upload documents' });
    }
  };

  const formData = deductionHeadsList?.length
    ? [...deductionHeadsList]
        .sort((a, b) => {
          return a.name.localeCompare(b.name);
        })
        .map((deductionHead) => {
          const currDeclrAmt = dedecHeadDeclrAmtsMap[deductionHead?.id] || 0;
          const showItag = secRefDetailsMap && secRefDetailsMap?.[deductionHead.id];
          return {
            deductionHeadID: deductionHead.id,
            statutoryReferenceSection: deductionHead.statutoryReferenceSection,
            showItag,
            investmentHead: `${deductionHead?.name} (${deductionHead?.statutoryReferenceSection}) 
            (${deductionHead?.id})`,
            dataOriginalTitle: deductionHead?.statutoryReferenceSection,
            dataContent: secRefDetailsMap[deductionHead.id],
            actualDeclairedAmount: currDeclrAmt,
          };
        })
    : [];

  let employeeDetails = {};
  if (employeeDeductionDeclarationInstance?.declarationProofStatus) {
    if (employeeDeductionDeclarationInstance?.declarationProofStatus === enumValues.PAYROLL_OPEN_FOR_PROOF_SUBMISSION) {
      employeeDetails = {
        name: employeeDeductionDeclarationInstance?.employee?.fullName || '',
        did: employeeDeductionDeclarationInstance?.id || '',
        financialYear: employeeDeductionDeclarationInstance?.financialYear || '',
        assesmentYear: employeeDeductionDeclarationInstance?.assessmentYear || '',
        eid: docsListData?.employeeId || '',
      };
    } else {
      addToast({
        type: 'error',
        title: 'ERROR',
        message: `Warning! Investment proofs submission is not allowed at this time.
       Please contact your administrator for further details.`,
      });
    }
  }

  const handleSubmission = async () => {
    const bodyFormData = new FormData();
    bodyFormData.append('eid', employeeDeductionDeclarationInstance?.employee?.id);
    bodyFormData.append('did', employeeDeductionDeclarationInstance?.id);
    try {
      const response = await formDataApi.post(`restPayIndex/submitEmployeeInvestmentProofs`, bodyFormData);
      if (response?.data?.status !== 'ERROR') {
        addToast({
          type: 'success',
          title: 'SUCCESS',
          message: response?.data?.message || 'Investment proofs submitted successfully',
        });
      }
    } catch (submmitError) {
      addToast({
        type: 'error',
        title: 'ERROR',
        message: submmitError?.message || 'Failed to submit investment proof',
      });
    }
  };

  const handleCancel = () => {
    navigate('/home/mypay/tds-declaration');
  };

  return (
    <UploadInvestmentProofsTemplate
      formData={formData}
      employeeDetails={employeeDetails}
      handleUpload={handleUpload}
      handleSubmitProofs={handleSubmission}
      userHasProofSubmitPermission={userHasProofSubmitPermission}
      userhasProofUploadPermission={userhasProofUploadPermission}
      handleCancel={handleCancel}
    />
  );
};

export default InvestmentProofPage;
